import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, Loader2, KeyRound, Lock, CreditCard } from "lucide-react";

/**
 * Reusable manager-approval dialog.
 * Verifies via POST /auth/authorize that someone holding `permission` approves.
 * onApproved(cred, approver) receives the credential object (without permission)
 * so the caller can forward it to the mutating endpoint if needed.
 */
export default function ApprovalDialog({ open, onClose, permission, title, description, onApproved }) {
  const [method, setMethod] = useState("pin");
  const [pin, setPin] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [card, setCard] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setPin(""); setPassword(""); setEmail(""); setCard(""); };

  // GÜVENLİK: dialog her açıldığında kimlik alanları SIFIRLANIR — başkasının girdiği kod/PIN
  // ekranda kalmaz (ortak terminalde yanlış kişi adına onay riskini önler).
  useEffect(() => { if (open) { setMethod("pin"); reset(); } }, [open]);

  const submit = async () => {
    setLoading(true);
    const cred = { permission };
    if (method === "pin") cred.pin = pin;
    else if (method === "password") { cred.password = password; if (email) cred.email = email; }
    else if (method === "card") cred.card = card;
    try {
      const { data } = await api.post("/auth/authorize", cred);
      toast.success(`Onaylayan: ${data.approver.name} · ${data.approver.role_label}`);
      const { permission: _p, ...credOnly } = cred;
      reset();
      onApproved(credOnly, data.approver);
      onClose();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Onay reddedildi");
    } finally { setLoading(false); reset(); }
  };

  const Tab = ({ id, icon: Icon, label }) => (
    <button type="button" data-testid={`approval-method-${id}`} onClick={() => setMethod(id)}
      className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-xs font-semibold transition-colors ${method === id ? "bg-brand-green text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="approval-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <ShieldCheck className="h-5 w-5 text-brand-orange" /> {title || "Yetkili Onayı Gerekli"}
          </DialogTitle>
          <DialogDescription>{description || "Bu işlem için yetkili bir kişinin onayı gerekiyor."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Tab id="pin" icon={KeyRound} label="PIN" />
            <Tab id="password" icon={Lock} label="Şifre" />
            <Tab id="card" icon={CreditCard} label="Kart" />
          </div>
          {method === "pin" && (
            <div><label className="overline">Yetkili PIN</label>
              <Input data-testid="approval-pin" type="password" inputMode="numeric" value={pin} autoFocus autoComplete="new-password"
                onChange={(e) => setPin(e.target.value)} placeholder="****" className="mt-1 tabular text-center text-lg tracking-widest" /></div>
          )}
          {method === "password" && (
            <>
              <div><label className="overline">E-posta (opsiyonel)</label>
                <Input data-testid="approval-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="yetkili@groot.com" className="mt-1" /></div>
              <div><label className="overline">Şifre</label>
                <Input data-testid="approval-password" type="password" value={password} autoComplete="new-password" onChange={(e) => setPassword(e.target.value)} placeholder="••••••" className="mt-1" /></div>
            </>
          )}
          {method === "card" && (
            <div><label className="overline">Kart Kodu</label>
              <Input data-testid="approval-card" value={card} autoFocus autoComplete="off" onChange={(e) => setCard(e.target.value)} placeholder="Kartı okutun / kodu girin" className="mt-1 font-mono" /></div>
          )}
          <Button data-testid="approval-submit" onClick={submit} disabled={loading} className="w-full bg-brand-orange text-white hover:bg-orange-600">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Onayla
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
